import React, { useMemo, useState } from 'react';
import { View, Text, Pressable, StyleSheet, TextInput, ActivityIndicator } from 'react-native';
import { AlertTriangle, CheckCircle2, Plus, Trash2 } from '../lib/icons';
import { shadows } from '../lib/styles';

interface HomeownerOption {
  id?: string;
  title: string;
  provider_name?: string;
  estimated_cost?: string;
  estimated_timeline?: string;
  why_this_option: string;
}

interface AnalyzeRecommendationsPanelProps {
  analysisData?: any;
  onOverride: (
    options: HomeownerOption[],
    recommendedOptionId: string,
    overrideReason: string
  ) => Promise<void>;
  disabled?: boolean;
}

const createBlankOption = (index: number): HomeownerOption => ({
  id: `custom-option-${Date.now()}-${index}`,
  title: '',
  provider_name: '',
  estimated_cost: '',
  estimated_timeline: '',
  why_this_option: '',
});

export const AnalyzeRecommendationsPanel: React.FC<AnalyzeRecommendationsPanelProps> = ({
  analysisData,
  onOverride,
  disabled = false,
}) => {
  const aiOptions = useMemo(() => {
    const options = analysisData?.homeowner_options;
    return Array.isArray(options) ? options : [];
  }, [analysisData]);

  const aiRecommendedId = analysisData?.recommended_option_id || '';
  const hasHumanOverride = analysisData?.human_override === true;

  const [overrideReason, setOverrideReason] = useState('');
  const [customOptions, setCustomOptions] = useState<HomeownerOption[]>([createBlankOption(1)]);
  const [recommendedCustomId, setRecommendedCustomId] = useState<string>(customOptions[0].id || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateOption = (id: string, key: keyof HomeownerOption, value: string) => {
    setCustomOptions((prev) => prev.map((option) => (
      option.id === id ? { ...option, [key]: value } : option
    )));
  };

  const addOption = () => {
    const newOption = createBlankOption(customOptions.length + 1);
    setCustomOptions((prev) => [...prev, newOption]);
    if (!recommendedCustomId) {
      setRecommendedCustomId(newOption.id || '');
    }
  };

  const removeOption = (id: string) => {
    setCustomOptions((prev) => prev.filter((option) => option.id !== id));
    if (recommendedCustomId === id) {
      const next = customOptions.find((option) => option.id !== id);
      setRecommendedCustomId(next?.id || '');
    }
  };

  const resetForm = () => {
    const resetOption = createBlankOption(1);
    setOverrideReason('');
    setCustomOptions([resetOption]);
    setRecommendedCustomId(resetOption.id || '');
    setError(null);
  };

  const validate = (): string | null => {
    if (!overrideReason.trim()) {
      return 'Please provide a reason for the override.';
    }

    if (customOptions.length === 0) {
      return 'Please add at least one homeowner option.';
    }

    const hasInvalidOption = customOptions.some((option) => !option.title.trim() || !option.why_this_option.trim());
    if (hasInvalidOption) {
      return 'Each option must include a title and recommendation rationale.';
    }

    if (!recommendedCustomId || !customOptions.some((option) => option.id === recommendedCustomId)) {
      return 'Please select which custom option you recommend.';
    }

    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      await onOverride(
        customOptions.map((option, idx) => ({
          ...option,
          id: option.id || `human-option-${idx + 1}`,
        })),
        recommendedCustomId,
        overrideReason.trim()
      );
      resetForm();
    } catch (err: any) {
      setError(err?.message || 'Failed to apply analyze override');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <View style={styles.container}>
      {aiOptions.length > 0 && (
        <View style={styles.aiOptionsContainer}>
          <Text style={styles.sectionTitle}>AI Homeowner Options</Text>
          {hasHumanOverride && (
            <View style={styles.overrideBadge}>
              <AlertTriangle size={14} color="#B45309" />
              <Text style={styles.overrideBadgeText}>Human override currently active</Text>
            </View>
          )}
          {aiOptions.map((option: any) => {
            const isRecommended = option.id === aiRecommendedId;
            return (
              <View key={option.id || option.title} style={[styles.aiOptionCard, isRecommended && styles.aiOptionCardRecommended]}>
                <View style={styles.aiOptionHeader}>
                  <Text style={styles.aiOptionTitle}>{option.title}</Text>
                  {isRecommended && (
                    <View style={styles.recommendedBadge}>
                      <CheckCircle2 size={13} color="#16A34A" />
                      <Text style={styles.recommendedBadgeText}>AI Recommended</Text>
                    </View>
                  )}
                </View>
                {option.provider_name ? (
                  <Text style={styles.aiOptionMeta}>Provider: {option.provider_name}</Text>
                ) : null}
                {(option.estimated_cost || option.estimated_timeline) && (
                  <Text style={styles.aiOptionMeta}>
                    Cost: {option.estimated_cost || 'Unknown'} | Timeline: {option.estimated_timeline || 'Unknown'}
                  </Text>
                )}
                {option.why_this_option ? (
                  <Text style={styles.aiOptionWhy}>{option.why_this_option}</Text>
                ) : null}
              </View>
            );
          })}
        </View>
      )}

      <View style={styles.overrideContainer}>
        <View style={styles.overrideHeader}>
          <AlertTriangle size={16} color="#B45309" />
          <Text style={styles.overrideTitle}>Override with Human Options</Text>
        </View>

        <View style={styles.reasonContainer}>
          <Text style={styles.label}>Reason for override *</Text>
          <TextInput
            style={styles.reasonInput}
            value={overrideReason}
            onChangeText={setOverrideReason}
            placeholder="Explain why the AI recommendation should be replaced..."
            multiline
            numberOfLines={3}
            textAlignVertical="top"
            editable={!disabled && !submitting}
          />
        </View>

        <View style={styles.optionsHeader}>
          <Text style={styles.sectionTitle}>Custom Homeowner Options</Text>
          <Pressable
            style={[styles.addButton, (disabled || submitting) && styles.buttonDisabled]}
            onPress={addOption}
            disabled={disabled || submitting}
          >
            <Plus size={14} color="#2563EB" />
            <Text style={styles.addButtonText}>Add Option</Text>
          </Pressable>
        </View>

        {customOptions.map((option, idx) => {
          const optionId = option.id || `option-${idx + 1}`;
          const isRecommended = recommendedCustomId === optionId;
          return (
            <View key={optionId} style={styles.customOptionCard}>
              <View style={styles.customOptionHeader}>
                <Pressable
                  style={styles.radioRow}
                  onPress={() => setRecommendedCustomId(optionId)}
                  disabled={disabled || submitting}
                >
                  <View style={[styles.radioCircle, isRecommended && styles.radioCircleSelected]} />
                  <Text style={styles.radioLabel}>Recommend this option</Text>
                </Pressable>
                {customOptions.length > 1 && (
                  <Pressable
                    style={styles.removeButton}
                    onPress={() => removeOption(optionId)}
                    disabled={disabled || submitting}
                  >
                    <Trash2 size={14} color="#DC2626" />
                  </Pressable>
                )}
              </View>

              <TextInput
                style={styles.textInput}
                value={option.title}
                onChangeText={(value) => updateOption(optionId, 'title', value)}
                placeholder="Option title (e.g., Premium local specialist)"
                editable={!disabled && !submitting}
              />
              <TextInput
                style={styles.textInput}
                value={option.provider_name || ''}
                onChangeText={(value) => updateOption(optionId, 'provider_name', value)}
                placeholder="Provider name (optional)"
                editable={!disabled && !submitting}
              />
              <View style={styles.inlineInputs}>
                <TextInput
                  style={[styles.textInput, styles.inlineInput]}
                  value={option.estimated_cost || ''}
                  onChangeText={(value) => updateOption(optionId, 'estimated_cost', value)}
                  placeholder="Estimated cost"
                  editable={!disabled && !submitting}
                />
                <TextInput
                  style={[styles.textInput, styles.inlineInput]}
                  value={option.estimated_timeline || ''}
                  onChangeText={(value) => updateOption(optionId, 'estimated_timeline', value)}
                  placeholder="Estimated timeline"
                  editable={!disabled && !submitting}
                />
              </View>
              <TextInput
                style={[styles.textInput, styles.multilineInput]}
                value={option.why_this_option}
                onChangeText={(value) => updateOption(optionId, 'why_this_option', value)}
                placeholder="Why this option is being presented to the homeowner"
                multiline
                numberOfLines={3}
                textAlignVertical="top"
                editable={!disabled && !submitting}
              />
            </View>
          );
        })}

        {error && (
          <View style={styles.errorContainer}>
            <AlertTriangle size={14} color="#DC2626" />
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}

        <View style={styles.actionsRow}>
          <Pressable
            style={[styles.actionButton, styles.cancelButton]}
            onPress={resetForm}
            disabled={submitting}
          >
            <Text style={styles.cancelText}>Reset</Text>
          </Pressable>
          <Pressable
            style={[styles.actionButton, styles.submitButton, (disabled || submitting) && styles.buttonDisabled]}
            onPress={handleSubmit}
            disabled={disabled || submitting}
          >
            {submitting ? (
              <>
                <ActivityIndicator size="small" color="#FFFFFF" />
                <Text style={styles.submitText}>Applying...</Text>
              </>
            ) : (
              <>
                <CheckCircle2 size={14} color="#FFFFFF" />
                <Text style={styles.submitText}>Apply Human Override</Text>
              </>
            )}
          </Pressable>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 14,
  },
  aiOptionsContainer: {
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 14,
    gap: 10,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  overrideBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#FFFBEB',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#FCD34D',
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  overrideBadgeText: {
    fontSize: 12,
    color: '#92400E',
    fontWeight: '500',
  },
  aiOptionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 10,
    gap: 6,
  },
  aiOptionCardRecommended: {
    borderColor: '#86EFAC',
    backgroundColor: '#F0FDF4',
  },
  aiOptionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
    alignItems: 'center',
  },
  aiOptionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
    flex: 1,
  },
  recommendedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  recommendedBadgeText: {
    fontSize: 11,
    color: '#15803D',
    fontWeight: '600',
  },
  aiOptionMeta: {
    fontSize: 12,
    color: '#6B7280',
  },
  aiOptionWhy: {
    fontSize: 13,
    color: '#374151',
    lineHeight: 18,
  },
  overrideContainer: {
    backgroundColor: '#FEF3C7',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#F59E0B',
    padding: 14,
    gap: 12,
    ...shadows.sm,
  },
  overrideHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  overrideTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#92400E',
  },
  reasonContainer: {
    gap: 6,
  },
  label: {
    fontSize: 13,
    color: '#374151',
    fontWeight: '500',
  },
  reasonInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    padding: 10,
    minHeight: 72,
    fontSize: 13,
    color: '#111827',
  },
  optionsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#EFF6FF',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#BFDBFE',
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  addButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#2563EB',
  },
  customOptionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 10,
    gap: 8,
  },
  customOptionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  radioRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  radioCircle: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    backgroundColor: '#FFFFFF',
  },
  radioCircleSelected: {
    borderColor: '#2563EB',
    backgroundColor: '#2563EB',
  },
  radioLabel: {
    fontSize: 12,
    color: '#374151',
    fontWeight: '500',
  },
  removeButton: {
    padding: 4,
  },
  textInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 13,
    color: '#111827',
  },
  inlineInputs: {
    flexDirection: 'row',
    gap: 8,
  },
  inlineInput: {
    flex: 1,
  },
  multilineInput: {
    minHeight: 64,
  },
  errorContainer: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    backgroundColor: '#FEE2E2',
    borderColor: '#FCA5A5',
    borderWidth: 1,
    borderRadius: 6,
    padding: 10,
  },
  errorText: {
    flex: 1,
    fontSize: 12,
    color: '#B91C1C',
  },
  actionsRow: {
    flexDirection: 'row',
    gap: 10,
  },
  actionButton: {
    flex: 1,
    borderRadius: 6,
    paddingVertical: 10,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 6,
  },
  cancelButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D1D5DB',
  },
  cancelText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
  },
  submitButton: {
    backgroundColor: '#D97706',
  },
  submitText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
});
